import { useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  List,
  LogOut,
  Maximize2,
  Minimize2,
  Monitor,
  Radio,
  Sun,
} from 'lucide-react';
import { ApiError, request } from '../api';
import type { Player } from '../domain/player';
import { isOpen, phaseLabels, type SimState } from '../domain/model';
import { incidentCode } from '../domain/incidentProfiles';
import { callsign, tacticalStatus } from '../domain/radio';
import { osm, type MapProvider } from '../providers/maps';
import { time } from '../format';
import DispatchMap, { type MapFocus } from './DispatchMap';

type DisplayFocus = { incidentId: string | null; sequence: number };
type WakeHandle = {
  release: () => Promise<void>;
  addEventListener: (name: string, fn: () => void) => void;
};
type WakeNavigator = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeHandle> };
};

export default function MapDisplay({
  player,
  onExpired,
  onLogout,
}: {
  player: Player;
  onExpired: () => void;
  onLogout: () => Promise<void>;
}) {
  const [state, setState] = useState<SimState | null>(null);
  const [provider, setProvider] = useState<MapProvider>(osm);
  const [connected, setConnected] = useState(false);
  const [lastSeen, setLastSeen] = useState(0);
  const [wall, setWall] = useState(Date.now());
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [follow, setFollow] = useState(true);
  const [remoteFocus, setRemoteFocus] = useState<DisplayFocus | null>(null);
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const [panel, setPanel] = useState(true);
  const [catalog, setCatalog] = useState(false);
  const [catalogName, setCatalogName] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const [keepAwake, setKeepAwake] = useState(false);
  const [awake, setAwake] = useState(false);
  const [notice, setNotice] = useState('');
  const expired = useRef(onExpired);
  expired.current = onExpired;
  const latest = useRef(state);
  latest.current = state;
  useEffect(() => {
    let disposed = false;
    const events = new EventSource('/api/events');
    const alive = () => {
      setConnected(true);
      setLastSeen(Date.now());
    };
    events.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setState((current) =>
        !current || data.state.revision >= current.revision ? data.state : current,
      );
      setRemoteFocus((current) =>
        data.displayFocus?.sequence === current?.sequence ? current : data.displayFocus,
      );
      setError(data.storageError ?? '');
      alive();
    };
    events.addEventListener('heartbeat', alive);
    events.onerror = () => {
      setConnected(false);
      void request('auth/me').catch((caught) => {
        if (!disposed && caught instanceof ApiError && caught.status === 401) {
          events.close();
          expired.current();
        }
      });
    };
    void request<{ map: MapProvider | null }>('config')
      .then((data) => {
        if (!disposed && data.map) setProvider(data.map);
      })
      .catch(() => {});
    const timer = setInterval(() => setWall(Date.now()), 3000);
    return () => {
      disposed = true;
      events.close();
      clearInterval(timer);
    };
  }, [player.id]);
  useEffect(() => {
    if (!follow || !remoteFocus) return;
    const incident = latest.current?.incidents.find((item) => item.id === remoteFocus.incidentId);
    setSelectedId(incident?.id ?? '');
    setVehicleId('');
    if (incident) setFocus({ point: incident.location, sequence: Date.now(), zoom: 15 });
  }, [remoteFocus, follow]);
  useEffect(() => {
    const changed = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', changed);
    return () => document.removeEventListener('fullscreenchange', changed);
  }, []);
  useEffect(() => {
    if (!keepAwake) {
      setAwake(false);
      return;
    }
    let disposed = false;
    let handle: WakeHandle | undefined;
    let acquiring = false;
    const acquire = async () => {
      if (disposed || document.visibilityState !== 'visible' || handle || acquiring) return;
      acquiring = true;
      try {
        const next = await (navigator as WakeNavigator).wakeLock?.request('screen');
        if (!next) throw Error('unsupported');
        if (disposed) {
          await next.release();
          return;
        }
        handle = next;
        setAwake(true);
        next.addEventListener('release', () => {
          handle = undefined;
          if (!disposed) setAwake(false);
        });
      } catch {
        if (!disposed) {
          setAwake(false);
          setNotice(
            'Der Browser kann den Bildschirm gerade nicht wach halten. Bitte die Geräteeinstellung verwenden.',
          );
        }
      } finally {
        acquiring = false;
      }
    };
    void acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', acquire);
      void handle?.release().catch(() => {});
    };
  }, [keepAwake]);
  async function toggleFullscreen() {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (document.documentElement.requestFullscreen)
        await document.documentElement.requestFullscreen();
      else
        setNotice(
          'Vollbild wird von diesem Browser nicht unterstützt. Die Karte nutzt bereits die gesamte Browserfläche.',
        );
    } catch {
      setNotice(
        'Vollbild konnte nicht aktiviert werden. Bitte die Vollbildfunktion des Browsers verwenden.',
      );
    }
  }
  function select(id: string) {
    setFollow(false);
    setSelectedId(id);
    setVehicleId('');
    setCatalogName('');
    const incident = state?.incidents.find((item) => item.id === id);
    if (incident) setFocus({ point: incident.location, sequence: Date.now(), zoom: 15 });
  }
  const live = connected && wall - lastSeen < 35000;
  const open = state?.incidents.filter(isOpen) ?? [];
  const selected = state?.incidents.find((item) => item.id === selectedId);
  const vehicle = state?.vehicles.find((item) => item.id === vehicleId);
  const message = state?.radioMessages.at(-1);
  return (
    <div className="display-page">
      <header className="display-header">
        <div className="display-title">
          <Monitor size={24} />
          <span>
            <b>LAGEKARTE</b>
            <small>{state?.area.name ?? 'Verbindung zur Leitstelle'}</small>
          </span>
        </div>
        <div className={'display-live' + (live ? '' : ' offline')} role="status">
          <i />
          {live
            ? state?.running
              ? 'Live verbunden'
              : 'Live · Simulation pausiert'
            : 'Verbindung wird hergestellt …'}
        </div>
        <div className="display-actions">
          <button
            className="button secondary"
            aria-pressed={panel}
            aria-label="Einsätze"
            onClick={() => setPanel(!panel)}
          >
            <List size={17} />
            <span>Einsätze</span>
          </button>
          <button
            className="button secondary"
            aria-label={fullscreen ? 'Vollbild beenden' : 'Vollbild aktivieren'}
            onClick={() => void toggleFullscreen()}
          >
            {fullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
          <a className="button secondary" href="/leitstelle" aria-label="Zum Arbeitsplatz">
            <ExternalLink size={17} />
          </a>
          <button
            className="button secondary"
            aria-label="Auf diesem Gerät abmelden"
            onClick={() => void onLogout()}
          >
            <LogOut size={17} />
          </button>
        </div>
      </header>
      <div className={'display-body' + (panel ? '' : ' without-panel')}>
        <main className="display-map" aria-label="Live-Kartenbildschirm">
          {state ? (
            <DispatchMap
              state={live ? state : { ...state, running: false }}
              selectedId={selected?.id ?? ''}
              onSelect={select}
              provider={provider}
              focus={focus}
              onVehicleSelect={(id) => {
                setVehicleId(id ?? '');
                setPanel(true);
              }}
              catalogVisible={catalog}
              onCatalogToggle={() => setCatalog(!catalog)}
              onCatalogSelect={(entry) => {
                setCatalogName(entry.name + ' · ' + entry.service);
                setPanel(true);
              }}
            />
          ) : (
            <div className="empty">Lagekarte wird geladen …</div>
          )}
          {!panel && notice && (
            <div className="connection-banner" role="status">
              {notice}
            </div>
          )}
          {(!live || error) && (
            <div className="connection-banner" role="alert">
              {error ||
                (state
                  ? 'Verbindung unterbrochen – letzter Stand. Wiederverbindung erfolgt automatisch.'
                  : 'Warte auf Live-Daten der Leitstelle …')}
            </div>
          )}
        </main>
        {panel && (
          <aside className="display-panel" aria-label="Einsätze auf dem Kartenbildschirm">
            <div className="display-panel-heading">
              <span className="eyebrow">LIVE-LAGE</span>
              <h1>{open.length} aktive Einsätze</h1>
              <p>{state ? time(state.now) : '—'} · Simulationszeit</p>
            </div>
            <label className="switch-row">
              <span>
                PC-Auswahl folgen
                <small>
                  {follow
                    ? 'Die Karte folgt dem am PC ausgewählten Einsatz.'
                    : 'Eigene Kartenauswahl auf diesem Gerät.'}
                </small>
              </span>
              <input
                type="checkbox"
                checked={follow}
                onChange={(event) => setFollow(event.target.checked)}
              />
            </label>
            <button
              className="display-wake"
              aria-pressed={keepAwake}
              onClick={() => {
                setKeepAwake(!keepAwake);
                setNotice('');
              }}
            >
              <Sun size={16} />
              {awake
                ? 'Bildschirm bleibt wach'
                : keepAwake
                  ? 'Wachhalten angefordert'
                  : 'Bildschirm wach halten'}
            </button>
            {notice && (
              <p className="display-notice" role="status">
                {notice}
              </p>
            )}
            {catalogName && (
              <div className="display-detail">
                <b>{catalogName}</b>
                <button className="text-button" onClick={() => setCatalogName('')}>
                  Schließen
                </button>
              </div>
            )}
            {vehicle && (
              <div className="display-detail">
                <b>{callsign(vehicle)}</b>
                <p>
                  Status {vehicle.status} · {tacticalStatus[vehicle.status]}
                </p>
                <button className="text-button" onClick={() => setVehicleId('')}>
                  Schließen
                </button>
              </div>
            )}
            {selected && (
              <section className="display-detail" aria-label="Ausgewählter Einsatz">
                <span className="eyebrow">
                  E {selected.number} · {incidentCode(selected)}
                </span>
                <h2>{selected.title}</h2>
                <b>{selected.address}</b>
                <p>{selected.district}</p>
                <span className={'phase ' + selected.phase}>{phaseLabels[selected.phase]}</span>
                <p>{selected.report}</p>
                <small>
                  {state?.vehicles.filter((item) => item.incidentId === selected.id).length} Kräfte
                  zugewiesen
                </small>
                <ul>
                  {selected.timeline.slice(-3).map((item) => (
                    <li key={item.id}>
                      <time>{time(item.at)}</time> {item.text}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <div className="display-incident-list">
              {open.map((incident) => (
                <button
                  key={incident.id}
                  className={'incident-row' + (selected?.id === incident.id ? ' selected' : '')}
                  onClick={() => select(incident.id)}
                >
                  <span className="eyebrow">
                    E {incident.number} · {incidentCode(incident)}
                  </span>
                  <b>{incident.title}</b>
                  <span>{incident.address}</span>
                  <small>{phaseLabels[incident.phase]}</small>
                </button>
              ))}
              {state && !open.length && (
                <p className="empty">Keine offenen Einsätze. Neue Lagen erscheinen automatisch.</p>
              )}
            </div>
          </aside>
        )}
      </div>
      <footer className="display-footer">
        <Radio size={16} />
        <span>
          {message?.transmissions.map((part) => part.text).join(' ') ??
            'Empfang bereit · neue Meldungen erscheinen hier'}
        </span>
        <b>{state?.vehicles.filter((item) => item.journey).length ?? 0} auf Fahrt</b>
      </footer>
    </div>
  );
}
